import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { KPI_LABELS, type KPIMetric } from '../../../types/dashboard-widgets';
import { ResponsiveAmount, AccountCodeLink } from '../../ui/ResponsiveAmount';

interface KPIWidgetProps {
  config: {
    metric?: KPIMetric;
    showTrend?: boolean;
  };
  data: any;
}

/**
 * Mapping des métriques KPI vers les codes de compte SYSCOHADA
 */
const KPI_ACCOUNT_CODES: Partial<Record<KPIMetric, string>> = {
  revenue: '7',        // Classe 7 - Produits
  expenses: '6',       // Classe 6 - Charges
  total_cash: '5',     // Classe 5 - Trésorerie
  cash_on_hand: '571', // Caisse
  bank_balance: '512', // Banques
  total_assets: '2',   // Classe 2 - Immobilisations
  total_liabilities: '1', // Classe 1 - Capitaux
};

// Mapping des métriques vers les chemins dans les données du backend
const getMetricValue = (data: any, metric: KPIMetric): number => {
  if (!data) return 0;
  
  const paths: Record<KPIMetric, () => number> = {
    revenue: () => data.profitAndLoss?.current?.revenue ?? 0,
    expenses: () => data.profitAndLoss?.current?.expenses ?? 0,
    net_profit: () => data.profitAndLoss?.current?.netProfit ?? 0,
    gross_profit: () => data.profitAndLoss?.current?.grossProfit ?? 0,
    profit_margin: () => data.profitAndLoss?.current?.profitMargin ?? 0,
    total_assets: () => data.financialPosition?.balanceSheet?.totalAssets ?? 0,
    total_liabilities: () => data.financialPosition?.balanceSheet?.totalLiabilities ?? 0,
    total_equity: () => data.financialPosition?.balanceSheet?.totalEquity ?? 0,
    net_assets: () => data.financialPosition?.balanceSheet?.netAssets ?? 0,
    total_cash: () => data.cashPosition?.totalCash ?? 0,
    cash_on_hand: () => data.cashPosition?.cashOnHand ?? 0,
    bank_balance: () => data.cashPosition?.bankBalance ?? 0,
    working_capital: () => data.financialPosition?.ratios?.workingCapital ?? 0,
    current_ratio: () => data.financialPosition?.ratios?.currentRatio ?? 0,
    debt_equity_ratio: () => data.financialPosition?.ratios?.debtEquityRatio ?? 0
  };
  
  return paths[metric]?.() ?? 0;
};

export function KPIWidget({ config, data }: KPIWidgetProps) {
  const metric = config.metric || 'revenue';
  const info = KPI_LABELS[metric] ?? { label: metric, format: 'currency' };
  const value = getMetricValue(data, metric);
  const accountCode = KPI_ACCOUNT_CODES[metric];
  
  // Get trend from backend data if available
  const getTrend = () => {
    if (!config.showTrend || !data) return null;
    
    // Try to get trend from backend comparison data
    const comparison = data.profitAndLoss?.comparison;
    if (comparison) {
      const previousValue = getMetricValue({ profitAndLoss: { current: comparison } }, metric);
      if (previousValue !== 0) {
        const change = ((value - previousValue) / Math.abs(previousValue)) * 100;
        return { value: change, isPositive: change >= 0 };
      }
    }
    
    // Fallback: no trend data available
    return null;
  };
  
  const trend = getTrend();

  return (
    <div className="flex flex-col justify-between h-full min-h-[80px]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">
          {info.label}
        </p>
        {accountCode && (
          <AccountCodeLink 
            code={accountCode} 
            variant="subtle"
          />
        )}
      </div>
      
      <div className="mt-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          {info.format === 'currency' ? (
            <ResponsiveAmount 
              value={value}
              size="2xl"
              className="font-bold"
              colorize={value < 0}
            />
          ) : info.format === 'percent' ? (
            <span className={`text-2xl font-bold tabular-nums ${value < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
              {value.toFixed(1)}%
            </span>
          ) : (
            <span className={`text-2xl font-bold tabular-nums ${value < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
              {value.toFixed(2)}
            </span>
          )}
          
          {trend && config.showTrend && (
            <span className={`inline-flex items-center text-xs font-medium tabular-nums ${
              trend.isPositive 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-600 dark:text-red-400'
            }`}>
              {trend.isPositive ? (
                <TrendingUp className="h-3 w-3 mr-0.5 flex-shrink-0" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-0.5 flex-shrink-0" />
              )}
              {Math.abs(trend.value).toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
